/**
 * The persisted trace of an activation `entitlementId` (PRO-13).
 *
 * `consumedAt` is a tombstone rather than a delete: the URL may still carry
 * the id after a successful auth, and a reload would otherwise re-capture an
 * id that was already linked. A *different* incoming id always overwrites the
 * whole record — a new activation wins.
 */
export interface IEntitlementRecord {
  id: string
  capturedAt: number
  consumedAt?: number
}

/**
 * Linking-failure reason tokens (PRO-9 / PRO-15). Stable identifiers emitted
 * by the backend next to `orderLinked: false` — never display text: copy
 * lives in the front, keyed per reason, and an unrecognized token falls back
 * to the generic message so the list can grow without coordinating releases.
 */
export const LINKING_REASONS = {
  ALREADY_CLAIMED: 'ENTITLEMENT_ALREADY_CLAIMED',
  TERMINATED: 'ENTITLEMENT_TERMINATED',
  NOT_FOUND: 'ENTITLEMENT_NOT_FOUND',
  NOT_CLAIMABLE: 'ENTITLEMENT_NOT_CLAIMABLE',
  PRODUCT_NOT_AVAILABLE: 'PRODUCT_NOT_AVAILABLE',
  INVALID_REQUEST: 'INVALID_REQUEST',
  LINKING_UNAVAILABLE: 'LINKING_UNAVAILABLE',
} as const

export type LinkingReason = (typeof LINKING_REASONS)[keyof typeof LINKING_REASONS]

/**
 * Reasons that can never resolve by retrying the same id: another user holds
 * it, or the operator canceled it. Once the user has *acknowledged* the
 * failure modal (see `consumeLinkingFailure`), these tombstone the stored
 * record so a dead id stops riding (and re-failing) every auth for the rest
 * of the TTL. Everything else keeps the record so a later auth retries —
 * including NOT_FOUND, deliberately: the id may simply not exist *yet*
 * (partner provisioning lag), and the generic copy tells the user to reopen
 * their activation link, which must therefore stay able to work. The
 * tombstone happens at acknowledgment, not detection, so a failure the user
 * was never shown (embedded auth panels suppress the modal) leaves the
 * record intact and a later auth both retries and informs.
 */
const TERMINAL_LINKING_REASONS: ReadonlySet<string> = new Set([
  LINKING_REASONS.ALREADY_CLAIMED,
  LINKING_REASONS.TERMINATED,
])

/**
 * Known tokens, for bounding what rides into telemetry keys: the reason is
 * backend-controlled, so anything outside this set is bucketed as 'unknown'
 * before being used in a Sentry latch key or tag — otherwise a backend that
 * varies the token per attempt would mint unbounded latch entries and burn
 * the report quota the latch exists to protect.
 */
const KNOWN_LINKING_REASONS: ReadonlySet<string> = new Set(Object.values(LINKING_REASONS))

export const isKnownLinkingReason = (reason: string | null): reason is string =>
  reason !== null && KNOWN_LINKING_REASONS.has(reason)

export const isTerminalLinkingReason = (reason: string | null): boolean =>
  reason !== null && TERMINAL_LINKING_REASONS.has(reason)

/**
 * Canonicalizes the raw `orderLinkedReason` from an auth response. Anything
 * that is not a plausible UPPER_SNAKE token — wrong type, empty, padded junk,
 * oversized — collapses to `null`, which the UI treats as the generic case.
 */
export const normalizeLinkingReason = (value: unknown): string | null => {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()

  return /^[A-Z][A-Z0-9_]{0,63}$/.test(trimmed) ? trimmed : null
}

/**
 * The in-memory record of the last linking failure (PRO-15): what
 * `withEntitlement` observed, for the AuthActor modal to render. `reason` is
 * already normalized — `null` means unknown/absent and renders the generic
 * copy. `source` is the auth flow tag ('login' | 'register' | ...).
 */
export interface ILinkingFailure {
  reason: string | null
  entitlementId: string
  source: string
}
